"use client";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@pex/ui-web";
import Image from "next/image";

const TESTIMONIALS = [
  {
    quote:
      "Project Exodus has an amazing structure geared towards helping attendees form meaningful accountability relationships. I felt welcome and at ease to share my struggles, experiences and failures. Free of judgment but equipped with tools to overcome codependency, identify destructive patterns and rethink how I make decisions.",
    name: "Bianca Pio",
    avatar: "/images/testimonials/Countryside_Scenes_219_Night_o9n23q.webp",
  },
  {
    quote:
      "I have received nothing but excellent, compassionate and transparent support from this group. So grateful to the leadership, encouraging topics chosen weekly and the enlightening unthreatening icebreakers. Thank you",
    name: "Elaine Thornton-Smith",
    avatar:
      "/images/testimonials/Famous_Buildings_467_National_Arts_Theatre_p2z60i.webp",
  },
  {
    quote:
      "At Project Exodus you get real support that makes a lasting and life changing difference. You will be able to make sense of your life and understand the different levels of your healing journey.",
    name: "Genevieve Rheeder",
    avatar:
      "/images/testimonials/Famous_Buildings_469_Times_Square_x52qv4.webp",
  },
  {
    quote:
      "PEX has allowed me to regain control of my life, walk a journey with Christ again and most of all welcomed me into a community of non judgemental like minded people.",
    name: "Danielle Bescoby",
    avatar: "/images/testimonials/Countryside_Scenes_211_Vaquero_9y0jas.webp",
  },
  {
    quote:
      "Their holistic approach to Recovery has changed my life. I was drawn in by their Christian based recovery program, and I kept coming back because they really do practise what they preach. I came in broken and desperate, found a place that felt safe, became a functioning part of the Project Exodus Community and now I live a life of Freedom. We recover for ourselves but never by ourselves.",
    name: "Bernice Nicholson",
    avatar: "/images/testimonials/Flowers_423_Rare_flower_a7vwuf.webp",
  },
  {
    quote:
      "Project Exodus has been a lifesaver for me and given me a second chance in life.",
    name: "Brian Dafel",
    avatar: "/images/testimonials/Forests_245_Treeline_at_an_angle_h4o34v.webp",
  },
  {
    quote:
      "Excellent and different approach to recovery. Super beneficial and empowering.",
    name: "Kaylan Nathulal",
    avatar:
      "/images/testimonials/Historical_Sites_108_Pyramids_of_Giza_1y412o.webp",
  },
  {
    quote:
      "Not only did Project Exodus awaken my mind to the potential that lies within me but also, they have given me the tools to fight the battles I face in my life and have taught me to take ownership of my life! Anyone who wants to bring out the best within themselves should surely be a part of Project Exodus!",
    name: "Ocean Lesley",
    avatar: "/images/testimonials/Beaches___Oceans_61_Sea_turtle_hy82ir.webp",
  },
  {
    quote:
      "Project Exodus has been a life changer. When I look 12 months back where I started, I cannot recognise the person I used to be. Highly recommend!!!",
    name: "Sibonelo Mantegu",
    avatar:
      "/images/testimonials/Bridges__Castles___Palaces_665_Brooklyn_Bridge_yph282.webp",
  },
  {
    quote:
      "Project Exodus are a group of people who are passionately in love with Jesus and have a great desire to see every person walk in the freedom that only God can provide. They've created a safe space for every person caught up in addiction or directly affected by addiction, to be vulnerable, overcome and thrive.",
    name: "Nicole Cilliers",
    avatar: "/images/testimonials/Flowers_456_Oleander_p9o6iz.webp",
  },
  {
    quote:
      "My husband and I have been with Project Exodus for about a year. The difference is tremendous. Addiction doesn't have the hold on him that it did and there's a marked change in behavioural habits on both of our sides. We have learned how to communicate and my hubby has learned how to talk instead of shut down.",
    name: "Claire Barnett",
    avatar:
      "/images/testimonials/Historical_Sites_132_Whakarewarewa_village_ha5jmy.webp",
  },
  {
    quote:
      "Phenomenal leadership and support for supporters and addicts, the passion, dedication and commitment to transforming lives is so evident. You can trust the team and leadership completely in the way the help you navigate the complex space of dealing with the impacts of and overcoming addiction.",
    name: "Michelle Pearton",
    avatar: "/images/testimonials/Countryside_Scenes_237_Fields_yebkqr.webp",
  },
] as const;

const STAR_KEYS = ["1", "2", "3", "4", "5"] as const;

function StarRating() {
  return (
    <div aria-label="5 out of 5 stars" className="flex gap-0.5" role="img">
      {STAR_KEYS.map((n) => (
        <svg
          aria-hidden="true"
          className="h-5 w-5 fill-amber-400 text-amber-400"
          key={n}
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

export function FindHelpTestimonialsCarousel() {
  return (
    <div className="relative">
      {/* Edge fade overlays — pointer-events-none so cards/buttons remain interactive */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 bottom-16 left-0 z-10 hidden w-32 sm:block"
        style={{
          background:
            "linear-gradient(to right, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.6) 40%, rgba(255,255,255,0) 100%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 right-0 bottom-16 z-10 hidden w-32 sm:block"
        style={{
          background:
            "linear-gradient(to left, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.6) 40%, rgba(255,255,255,0) 100%)",
        }}
      />
      <Carousel className="w-full" opts={{ loop: true }}>
        <CarouselContent className="-ml-4">
          {TESTIMONIALS.map((t) => (
            <CarouselItem className="pl-4 md:basis-1/2" key={t.name}>
              <div className="flex h-full flex-col gap-4 rounded-2xl border bg-card p-6 shadow-sm">
                <StarRating />
                <p className="body-base flex-1">{t.quote}</p>
                <div className="flex items-center gap-3">
                  <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full">
                    <Image
                      alt=""
                      className="object-cover"
                      fill
                      sizes="36px"
                      src={t.avatar}
                    />
                  </div>
                  <span className="font-semibold text-sm">{t.name}</span>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="mt-6 flex items-center gap-2 px-1">
          <CarouselPrevious className="static translate-y-0" />
          <CarouselNext className="static translate-y-0" />
        </div>
      </Carousel>
    </div>
  );
}
